import type { FC } from "react"
import React from "react"

import type { MapCameraChangedEvent } from "@vis.gl/react-google-maps"
import {
  APIProvider,
  AdvancedMarker,
  ControlPosition,
  Map,
  MapControl,
} from "@vis.gl/react-google-maps"
import { useRecoilState, useSetRecoilState } from "recoil"
import styled from "styled-components"

import {
  positionState,
  resetLocationZoomState,
  shouldRetainZoomState,
} from "~/containers/Proposal/state/site/site-state"
import EnvVars from "~/env-vars"

import ToggleButton from "../ToggleButton"

const ZOOM = 20

const GoogleMapInternal: FC = () => {
  const [position, setPosition] = useRecoilState(positionState)
  const setZoom = useSetRecoilState(resetLocationZoomState)
  const [shouldRetainZoom, setShouldRetainZoom] = useRecoilState(
    shouldRetainZoomState,
  )

  const googlePosition = {
    lat: position[0],
    lng: position[1],
  }
  if (googlePosition.lat === 0 && googlePosition.lng === 0) {
    return null
  }

  const onRetainZoomToggle = () => {
    setShouldRetainZoom(!shouldRetainZoom)
  }

  const onZoomChanged = (e: MapCameraChangedEvent) => {
    setZoom(e.detail.zoom)
  }

  return (
    <Wrapper>
      <section>
        <APIProvider apiKey={EnvVars.GOOGLE_MAPS_API_KEY}>
          <Map
            defaultCenter={googlePosition}
            mapId={`map-id-satellite`}
            mapTypeId={`satellite`}
            defaultZoom={ZOOM}
            defaultTilt={0}
            disableDefaultUI={true}
            keyboardShortcuts={false}
            zoomControl={true}
            mapTypeControl={true}
            onZoomChanged={onZoomChanged}
          >
            <AdvancedMarker
              position={googlePosition}
              draggable={true}
              onDrag={(e) =>
                setPosition([e.latLng?.lat() ?? 0, e.latLng?.lng() ?? 0])
              }
            />
            <MapControl position={ControlPosition.BLOCK_START_INLINE_END}>
              <RetainZoomContainer>
                <ToggleButton
                  label="Retain Zoom *"
                  isActive={shouldRetainZoom}
                  setAction={onRetainZoomToggle}
                />
              </RetainZoomContainer>
            </MapControl>
          </Map>
        </APIProvider>
      </section>
      <footer>* Retaining the zoom may result in a lower quality image</footer>
    </Wrapper>
  )
}

export const GoogleMap = React.memo(GoogleMapInternal)

const Wrapper = styled.main`
  position: relative;
  height: calc(100vh - 400px);
  min-height: 400px;
  width: 100%;
  > section {
    padding: 10px 10px 0px 10px;
    position: relative;
    width: 100%;
    height: 96%;
    button,
    label {
      font-size: var(--step--2) !important;
    }
  }
  > footer {
    position: relative;
    bottom: 0;
    right: 0;
    font-size: var(--step--2);
    text-align: right;
  }
`

const RetainZoomContainer = styled.div`
  background-color: white;
  border-radius: 2px;
  border: 1px solid #dfdddd;
  padding: 10px;
  margin-top: 10px;
  margin-right: 10px;
  > div {
    > p {
      font-family: Arial, Helvetica, sans-serif;
      font-weight: 600;
      color: #2e2e2e;
    }
  }
`
